import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';

export type BillingType = 'subscription' | 'service_fee' | 'revenue_share';
export type BillingStatus = 'active' | 'expired' | 'suspended' | 'replaced';

export interface CountyBillingConfig {
  id: number;
  county_id: number;
  type: BillingType;
  status: BillingStatus;
  starts_at: string | null;
  ends_at: string | null;
  is_trial: boolean;
  subscription_amount: string | null;
  subscription_currency: string;
  service_fee_mode: 'percentage' | 'tiered' | null;
  service_fee_percentage: string | null;
  service_fee_tiers: Array<{ min: number; max: number | null; fee: number }> | null;
  revenue_share_provider_percentage: string | null;
  revenue_share_county_percentage: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CountyBillingSettlement {
  id: number;
  county_id: number;
  billing_config_id: number;
  period_start: string;
  period_end: string;
  gross_collected: string;
  provider_share_total: string;
  county_share_total: string;
  transaction_count: number;
  status: 'pending' | 'paid_out' | 'cancelled';
  paid_out_at: string | null;
  payout_reference: string | null;
  notes: string | null;
  created_at: string;
}

export type CountyBillingStatus =
  | 'no_config'
  | 'active'
  | 'expiring_soon'
  | 'due_today'
  | 'expired'
  | 'suspended'
  | 'replaced';

export interface CountyBillingStatusResponse {
  status: CountyBillingStatus;
  config: CountyBillingConfig | null;
  days_remaining: number | null;
  is_trial: boolean;
}

class CountyBillingService {
  private base = '/admin/superadmin/counties';

  async get(countyId: number): Promise<ApiResponse<{ active: CountyBillingConfig | null; history: CountyBillingConfig[] }>> {
    return apiClient.get(`${this.base}/${countyId}/billing`);
  }

  /**
   * Read the billing status for the currently logged-in county admin.
   * Lives outside the billing-active gate so an expired county can still see why it's blocked.
   */
  async myStatus(): Promise<ApiResponse<CountyBillingStatusResponse>> {
    return apiClient.get('/admin/county/billing-status');
  }

  async create(countyId: number, payload: Record<string, unknown>): Promise<ApiResponse<CountyBillingConfig>> {
    return apiClient.post(`${this.base}/${countyId}/billing`, payload);
  }

  async extend(countyId: number, ends_at: string, notes?: string): Promise<ApiResponse<CountyBillingConfig>> {
    return apiClient.post(`${this.base}/${countyId}/billing/extend`, { ends_at, notes });
  }

  async listSettlements(countyId: number, page = 1): Promise<ApiResponse<PaginatedResponse<CountyBillingSettlement>>> {
    return apiClient.get(`${this.base}/${countyId}/billing/settlements?page=${page}`);
  }

  async generateSettlement(countyId: number, period_start: string, period_end: string): Promise<ApiResponse<CountyBillingSettlement>> {
    return apiClient.post(`${this.base}/${countyId}/billing/settlements`, { period_start, period_end });
  }

  async markSettlementPaid(
    countyId: number,
    settlementId: number,
    payload: { payout_reference: string; paid_out_at?: string; notes?: string },
  ): Promise<ApiResponse<CountyBillingSettlement>> {
    return apiClient.patch(`${this.base}/${countyId}/billing/settlements/${settlementId}/mark-paid`, payload);
  }

  /**
   * Cross-county settlements report (superadmin).
   */
  async report(params: {
    county_id?: number;
    type?: BillingType;
    status?: 'pending' | 'paid_out' | 'cancelled';
    period_start?: string;
    period_end?: string;
    per_page?: number;
    page?: number;
  } = {}): Promise<ApiResponse<BillingReportResponse>> {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const s = qs.toString();
    return apiClient.get(`/admin/superadmin/billing-report${s ? `?${s}` : ''}`);
  }

  /**
   * County-side: my settlements (read-only).
   */
  async mySettlements(params: { status?: string; per_page?: number; page?: number } = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const s = qs.toString();
    return apiClient.get(`/admin/county/billing/settlements${s ? `?${s}` : ''}`);
  }

  async mySettlementTransactions(settlementId: number, page = 1) {
    return apiClient.get(`/admin/county/billing/settlements/${settlementId}/transactions?page=${page}`);
  }
}

export interface BillingReportTotals {
  batch_count: number;
  transaction_count: number;
  gross_total: string;
  pending_owed_to_provider: string;
  pending_owed_to_counties: string;
  paid_to_provider: string;
  paid_to_counties: string;
}

export interface BillingReportPerCountyRow {
  county_id: number;
  county_name: string;
  county_code: string;
  billing_type: BillingType;
  batch_count: number;
  transaction_count: number;
  gross_collected: string;
  provider_total: string;
  county_total: string;
  pending_provider_total: string;
  pending_county_total: string;
  paid_provider_total: string;
  paid_county_total: string;
}

export interface BillingReportRow extends CountyBillingSettlement {
  county_name: string;
  county_code: string;
  billing_type: BillingType;
}

export interface BillingReportResponse {
  totals: BillingReportTotals;
  per_county: BillingReportPerCountyRow[];
  rows: PaginatedResponse<BillingReportRow>;
  whatsapp_volume_30d?: Array<{
    county_id: number;
    total: number;
    inbound_count: number;
    outbound_count: number;
  }>;
}

export const countyBillingService = new CountyBillingService();
